/**
 * i18n bootstrap. Creates the vue-i18n instance with EN as default + EL as
 * secondary. The actual locale gets set at runtime by `stores/locale.ts`
 * based on (1) authenticated user.locale, (2) localStorage, (3) browser, (4) 'en'.
 */
import { createI18n } from 'vue-i18n';
import en from './en';
import el from './el';

export type AppLocale = 'en' | 'el';

export const SUPPORTED_LOCALES: AppLocale[] = ['en', 'el'];
export const DEFAULT_LOCALE: AppLocale = 'en';
export const LOCALE_STORAGE_KEY = 'vistochat:locale';

/** Detect the best initial locale from browser hints + localStorage. */
export function detectInitialLocale(): AppLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  // 1. localStorage takes priority (a previous explicit choice).
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === 'en' || stored === 'el') return stored;
  } catch {
    // localStorage may be unavailable (privacy mode) — fall through.
  }

  // 2. Browser preference. navigator.language is ISO like "el-GR" or "en-US".
  const nav = (navigator.language || '').toLowerCase();
  if (nav.startsWith('el')) return 'el';

  return DEFAULT_LOCALE;
}

// `as const` was originally applied to en.ts / el.ts but we dropped it: it
// forced vue-i18n's generic-message type check to require el's literal strings
// to MATCH en's literal strings (impossible for any actual translation), which
// left `i18n.global` typed as `unknown` and broke every consumer.
//
// Key parity is verified independently — see scripts/i18n-key-parity.ts in the
// API for a one-shot diff (1188 keys match in both locales).
export const i18n = createI18n({
  legacy: false,                 // Composition API mode (we use <script setup>)
  locale: detectInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en, el },
  missingWarn: false,
  fallbackWarn: false,
  warnHtmlMessage: false,
});

export default i18n;
